import type { TFunction } from "@/i18n/catalog";

/**
 * The waste-type dimension stored on `waste_events.waste_type` and bucketed by
 * the stats RPC: five state-based types plus `unassigned` (POS2-894 freeze).
 *
 * Mirrored on the backend in `backend/app/schemas/waste.py` (`WasteType`).
 */
export const WASTE_TYPES = [
  "overproduction",
  "spoilage",
  "preparation",
  "post_consumer",
  "packaging",
  "unassigned",
] as const;

export type WasteType = (typeof WASTE_TYPES)[number];

/**
 * Literal keys in a lookup table, never a key assembled at runtime — `check-i18n.mjs` resolves
 * keys statically, so a built key is invisible to it (POS2-1134 CP4a).
 *
 * The English record stays exported for the two staff-only surfaces that read it: `/admin/*` and
 * `/labelling` are English by pinned decision 4, and passing them a `t` would translate a screen
 * the decision says not to.
 */
export const WASTE_TYPE_LABEL_KEYS: Record<string, string> = {
  overproduction: "reference.wasteType.overproduction",
  spoilage: "reference.wasteType.spoilage",
  preparation: "reference.wasteType.preparation",
  post_consumer: "reference.wasteType.post_consumer",
  packaging: "reference.wasteType.packaging",
  unassigned: "reference.wasteType.unassigned",
};

/** English wording, for the staff-only surfaces above. */
export const WASTE_TYPE_LABELS: Record<string, string> = {
  overproduction: "Overproduction",
  spoilage: "Spoilage",
  preparation: "Preparation",
  post_consumer: "Post-consumer",
  packaging: "Packaging",
  unassigned: "Unassigned",
};

/** Solid chart fills, one per waste type. Muted, distinct hues. */
export const WASTE_TYPE_CHART_COLOR: Record<string, string> = {
  overproduction: "#f97316",
  spoilage: "#ef4444",
  preparation: "#f59e0b",
  post_consumer: "#3b82f6",
  packaging: "#8b5cf6",
  unassigned: "#9ca3af",
};

export function wasteTypeLabel(t: TFunction, key: string): string {
  const labelKey = WASTE_TYPE_LABEL_KEYS[key];
  return labelKey ? t(labelKey) : key;
}
